import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../topics/tasks/datepicker.css';
import { Calendar, Check, Trash2, X } from 'lucide-react';
import { useCalendarPreferences } from '../../hooks/useCalendarPreferences';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';

interface MobileDatePickerModalProps {
  isOpen: boolean;
  selectedDate: Date | null;
  onClose: () => void;
  onDateChange: (date: Date | null) => void;
  onClear?: () => void;
}

/**
 * A full-screen date picker modal optimized for mobile displays
 * Used to select dates in a consistent, touch-friendly way
 */
export function MobileDatePickerModal({
  isOpen,
  selectedDate,
  onClose,
  onDateChange,
  onClear,
}: MobileDatePickerModalProps) {
  const { datePickerLocale } = useCalendarPreferences();
  const { isNativeApp, statusBarHeight, navigationBarHeight } = useStatusBarInfo();

  // Handle date selection with explicit event handling
  const handleDateChange = useCallback(
    (date: Date) => {
      // Create a fresh Date object to avoid any reference issues
      const newDate = new Date(date.getTime());

      // Standardize time to noon to avoid timezone issues
      newDate.setHours(12, 0, 0, 0);

      // Apply the date selection and close
      onDateChange(newDate);
      onClose();
    },
    [onDateChange, onClose]
  );

  // Handle clear button click
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      // Clear the date
      if (onClear) {
        onClear();
      } else {
        onDateChange(null);
      }

      // Close the modal
      onClose();
    },
    [onClear, onDateChange, onClose]
  );

  // Handle selecting today
  const handleSelectToday = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      const today = new Date();
      today.setHours(12, 0, 0, 0);

      // Apply the date change
      onDateChange(today);

      // Close the modal
      onClose();
    },
    [onDateChange, onClose]
  );

  // Handle done button click
  const handleDoneClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (selectedDate) {
        const newDate = new Date(selectedDate.getTime());
        newDate.setHours(12, 0, 0, 0);
        onDateChange(newDate);
      }

      onClose();
    },
    [selectedDate, onDateChange, onClose]
  );

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-white z-[10000] flex flex-col">
      {/* Header - with status bar spacing */}
      <div
        className="px-4 border-b border-gray-200 flex justify-between items-center bg-gray-900 text-white"
        style={{
          paddingTop: isNativeApp ? `calc(${statusBarHeight}px + 0.75rem)` : '0.75rem',
          paddingBottom: '0.75rem',
        }}
      >
        <div className="flex items-center">
          <Calendar size={18} className="mr-2" />
          <h2 className="text-lg font-medium">Select Deadline</h2>
        </div>
        <button
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          className="text-white hover:text-white/80 transition-colors"
          aria-label="Close date picker"
        >
          <X size={20} />
        </button>
      </div>

      {/* Calendar Component - Main Content */}
      <div className="flex-1 overflow-y-auto flex flex-col justify-center items-center p-2">
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          minDate={new Date()} // This ensures selecting past dates is disabled
          inline
          className="w-full mx-auto"
          locale={datePickerLocale}
          shouldCloseOnSelect={false} // Don't auto-close, let our handler control it
          useWeekdaysShort={true}
          fixedHeight={true}
          wrapperClassName="w-full max-w-[350px]"
          // Highlight the selected date even if it's in the past
          highlightDates={[
            // Highlight today's date
            {
              'react-datepicker__day--highlighted-today': [new Date()],
            },
            // Highlight the selected date if it exists and is different from today
            ...(selectedDate && selectedDate.getTime() !== new Date().setHours(0, 0, 0, 0)
              ? [
                  {
                    'react-datepicker__day--highlighted-selected': [selectedDate],
                  },
                ]
              : []),
          ]}
          renderDayContents={(day, date) => {
            if (!date) return day;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Special handling for past selected date
            if (
              selectedDate &&
              date.getDate() === selectedDate.getDate() &&
              date.getMonth() === selectedDate.getMonth() &&
              date.getFullYear() === selectedDate.getFullYear()
            ) {
              // If this is the currently selected date and it's in the past
              if (date < today) {
                return (
                  <div className="flex items-center justify-center h-full relative">
                    <span className="font-extrabold text-white" style={{ fontSize: '110%' }}>
                      {day}
                    </span>
                  </div>
                );
              }
            }

            return day;
          }}
        />
      </div>

      {/* Footer with action buttons - with navigation bar spacing */}
      <div
        className="border-t border-gray-200 bg-gray-50"
        style={{
          padding: isNativeApp ? `1rem 1rem calc(${navigationBarHeight}px + 1rem) 1rem` : '1rem',
        }}
      >
        <div className="flex justify-between gap-3">
          <button
            type="button"
            onClick={handleClear}
            className="flex-1 py-2.5 px-4 flex justify-center items-center gap-2 bg-red-50 text-red-600 font-medium rounded-md border border-red-200"
          >
            <Trash2 size={16} />
            <span>Clear</span>
          </button>

          <button
            type="button"
            onClick={e => {
              handleSelectToday(e);
            }}
            className="flex-1 py-2.5 px-4 flex justify-center items-center gap-2 bg-blue-50 text-blue-600 font-medium rounded-md border border-blue-200"
          >
            <Calendar size={16} />
            <span>Today</span>
          </button>

          <button
            type="button"
            onClick={handleDoneClick}
            className="flex-1 py-2.5 px-4 flex justify-center items-center gap-2 bg-blue-600 text-white font-medium rounded-md"
          >
            <Check size={16} />
            <span>Done</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MobileDatePickerModal;
